import { IFacetOption } from 'shared/types/ConstructorFacet';
import { ITransformedCategory } from 'search/types/Category';

const recursiveFunc = (level, target) => {
  if (level?.value === target?.data?.parent) {
    if (level?.children && level.children.every((child) => child.value !== target.value)) {
      level.children.push(target);
    } else if (level) {
      level.children = [target];
    }
    return;
  }

  if (!level?.children) return;

  for (let j = 0; j < level?.children.length; j += 1) {
    recursiveFunc(level?.children?.[j], target);
  }
};

/**
 * [transformCategoryFacet] - takes in an array of facet options and transforms it into parent/children relationship
 * ex: input: [{value: 'all', data: {parent: ''}}, {value: 'apparel', data: {parent: 'all'}}, {value: 'bags': data: {parent: 'apparel}}, {value: 'pouches', data: {parent: 'bags'}}]
 * output: {value: 'all', data: {parent: ''}, children: [{value: 'apparel', data: {parent: 'all'}, children: [{value: 'bags': data: {parent: 'apparel}, children: [{value: 'pouches', data: {parent: 'bags'}}]}]}]}
 * @param categoryFacetOptions { IFacetOption }
 * @param topLevelCategory { string } the most top level category
 */
export const transformCategoryFacet = (
  categoryFacetOptions,
  topLevelCategory,
): ITransformedCategory => {
  if (!categoryFacetOptions || !topLevelCategory) return null;

  const parentCategory = categoryFacetOptions?.find((option) => option.value === topLevelCategory);

  if (parentCategory) {
    const result = {
      ...parentCategory,
    };

    for (const categoryFacetOption of categoryFacetOptions) {
      recursiveFunc(result, categoryFacetOption);
    }

    return result;
  }

  // these children are part of a parent that does not exist in web_groups
  const hasChildrenWithoutParent = categoryFacetOptions?.find(
    (option) => option.data?.parent === topLevelCategory,
  );
  if (!parentCategory && hasChildrenWithoutParent) {
    const result = {
      value: topLevelCategory,
      children: [],
      count: 0,
      display_name: topLevelCategory,
      status: '',
    };

    for (const categoryFacetOption of categoryFacetOptions) {
      if (categoryFacetOption.data?.parent === topLevelCategory) {
        result.children.push(categoryFacetOption);
      }
    }

    return result;
  }

  return null;
};

/**
 * [getAllNestedSelectedChildrenValues] - loop through the current category and all its children (and children's children, recursively)
 * and get all categories' values that are currently in selectedFilterValues. Returns an array of string values of selected nested sub-categories
 * @param option - the current category
 * @param selectedFilterValues { Array<string>}- array of selected category values
 */
export const getAllNestedSelectedChildrenValues = (
  option: ITransformedCategory,
  selectedFilterValues: Array<string>,
) => {
  const result = [];

  if (!Array.isArray(selectedFilterValues)) return result;

  const inner = (target) => {
    const checkSelected = (cat) => {
      if (selectedFilterValues.includes(cat.value)) {
        result.push(cat.value);
      }
    };

    if (!target?.children) return;

    for (let i = 0; i < target?.children?.length; i += 1) {
      checkSelected(target.children[i]);

      if (target.children[i].children) {
        inner(target.children[i]);
      }
    }
  };

  inner(option);
  return result;
};
/**
 * [getAllParents] - returns an array of string values of the current category's parent, the parent's parent and upwards until
 * there is no more parent
 * @param option - current category
 * @param flatCategoryList - flat list of all categories
 * @param selectedFilterValues {Array<string>}- currently selected filter values
 */

export const getAllParents = ({
  option,
  flatCategoryList,
  selectedFilterValues,
}: {
  option: ITransformedCategory;
  flatCategoryList: Array<IFacetOption>;
  selectedFilterValues: Array<string>;
}): Array<string> => {
  const result = [];

  if (!Array.isArray(selectedFilterValues)) return result;

  const recurse = (target) => {
    if (!target.data.parent) return;

    if (selectedFilterValues.includes(target.data.parent)) {
      result.push(target.data.parent);
    }

    const parentCategory = flatCategoryList.find((cat) => cat.value === target.data.parent);
    if (parentCategory) {
      recurse(parentCategory);
    }
  };

  recurse(option);

  return result;
};

/**
 * [getAllNestedChildrenValues] - loop through the current category and all its children (and children's children, recursively)
 * and get all categories selected. Returns an array of string values of selected nested sub-categories
 * @param category  { IFacetOption }- the current category
 */
export const getAllNestedChildrenValues = (category: ITransformedCategory) => {
  const result = [];

  const inner = (target) => {
    const checkSelected = (cat) => {
      if (cat.status === 'selected') {
        result.push(cat.value);
      }
    };

    if (!target?.children) return;

    for (let i = 0; i < target?.children?.length; i += 1) {
      checkSelected(target.children[i]);

      if (target.children[i].children) {
        inner(target.children[i]);
      }
    }
  };

  inner(category);
  return result;
};

/**
 * [isOnlySiblingSelected] - loop through the categoryFacet object and find the parent category. Check within the parent's children,
 * if there is only 1 child selected. If a child's children are selected, it counts the child as selected once. If yes, return true, else return false.
 * @param categoryFacet { IFacetOption } - the entire category facet object
 * @param parent { string }- the parent category we are looking for
 */
export const isOnlySiblingSelected = (categoryFacet: ITransformedCategory, parent: string) => {
  let count = 0;

  const inner = (target) => {
    const isOnlyOneChildSelected = (cat) => {
      if (!cat.children) return;

      for (let i = 0; i < cat.children.length; i += 1) {
        if (
          (cat.children[i].status === 'selected' && cat.children[i].data.parent === 'apparel') ||
          cat.children[i]?.children?.some((c) => c?.status === 'selected')
        ) {
          count += 1;
        }
        isOnlyOneChildSelected(cat.children[i]);
      }
    };

    for (let i = 0; i < target?.children?.length; i += 1) {
      if (target.children[i].value === parent) {
        isOnlyOneChildSelected(target.children[i]);
      }

      if (!target?.children) return;

      if (target.children[i].children) {
        inner(target.children[i]);
      }
    }
  };

  inner(categoryFacet);
  return count === 1;
};
/**
 * [isOnlyNestedSelected] - loop through the categoryFacet object and find the parent category. Check within the parent's children,
 * if there is only 1 child selected. If yes, return true, else return false.
 * @param categoryFacet { IFacetOption }
 * @param parent { string }
 */
export const isOnlyNestedSelected = (categoryFacet: ITransformedCategory, parent: string) => {
  let result = false;
  let count = 0;

  const inner = (target) => {
    const isOnlyOneChildSelected = (cat) => {
      if (!cat.children) return;

      for (let i = 0; i < cat.children.length; i += 1) {
        if (cat.children[i].status === 'selected') {
          count += 1;
        }
        isOnlyOneChildSelected(cat.children[i]);
      }

      result = count === 1;
    };

    for (let i = 0; i < target?.children?.length; i += 1) {
      if (target.children[i].value === parent) {
        isOnlyOneChildSelected(target.children[i]);
      }

      if (!target?.children) return;

      if (target.children[i].children) {
        inner(target.children[i]);
      }
    }
  };

  inner(categoryFacet);
  return result;
};
